package org.teddapp.project.ui.review

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Flashcard
import org.teddapp.project.ui.DecisionOption
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddDecision
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Révisions : la boîte de Leitner à six cases. Ce qu'on sait revient rarement, ce qu'on
 * oublie revient demain. Une carte à la fois, retournée d'un geste, deux réponses égales.
 */
@Composable
internal fun ReviewView(model: ReviewModel, compact: Boolean, modifier: Modifier = Modifier) {
    val h = if (compact) 16.dp else 28.dp
    Column(
        modifier = modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(start = h, end = h, top = 14.dp, bottom = 32.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f)) {
                Text("RÉVISIONS", style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(
                    text = when {
                        model.totalCards == 0 -> "Aucune carte"
                        model.dueCount == 0 -> "Rien à réviser aujourd'hui"
                        else -> "${model.remaining.size} carte${if (model.remaining.size > 1) "s" else ""} à revoir"
                    },
                    style = TeddType.Title2,
                    color = TeddTokens.Ink,
                )
            }
            TeddButton(
                text = "Nouvelle carte",
                onClick = { model.creating = true },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                leadingIcon = { TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.Ink) },
            )
        }

        if (model.skills.isNotEmpty()) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                FilterChip("Toutes", model.skillFilter == null) { model.skillFilter = null; model.restartSession() }
                model.skills.forEach { sk ->
                    val n = model.cards.count { it.is_deleted == 0L && it.skill_id == sk.id }
                    if (n > 0) {
                        FilterChip("${sk.name} · $n", model.skillFilter == sk.id) {
                            model.skillFilter = if (model.skillFilter == sk.id) null else sk.id
                            model.restartSession()
                        }
                    }
                }
            }
        }

        val card = model.current
        when {
            model.totalCards == 0 -> EmptyReview(onCreate = { model.creating = true })
            card != null -> ReviewCard(model, card)
            else -> SessionDone(model)
        }

        BoxesRow(model.boxes, model.intervals)

        if (model.totalCards > 0) {
            AllCardsList(model)
        }
    }

    if (model.creating || model.editing != null) {
        CardEditor(
            existing = model.editing,
            skills = model.skills,
            onClose = { model.creating = false; model.editing = null },
            onSave = { front, back, skillId, note -> model.save(model.editing, front, back, skillId, note) },
            onDelete = model.editing?.let { c -> { model.delete(c) } },
        )
    }
}

/** La carte du moment : sa face, puis son dos, puis deux réponses de même poids. */
@Composable
private fun ReviewCard(model: ReviewModel, card: Flashcard) {
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(24.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = buildString {
                    append("Case ${card.box} sur ${model.boxCount}")
                    model.skillName(card.skill_id)?.let { append(" · $it") }
                    if (card.lapses > 0) append(" · oubliée ${card.lapses} fois")
                },
                style = TeddType.Overline,
                color = TeddTokens.InkMuted,
                modifier = Modifier.weight(1f),
            )
            Text("${model.doneToday} vues", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp)
        }
        Spacer(modifier = Modifier.height(16.dp))
        Text(card.front, style = TeddType.Quote, color = TeddTokens.Ink, textAlign = TextAlign.Center, modifier = Modifier.fillMaxWidth())
        Spacer(modifier = Modifier.height(18.dp))
        if (!model.revealed) {
            TeddButton(
                text = "Retourner la carte",
                onClick = { model.reveal() },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Lg,
                modifier = Modifier.fillMaxWidth(),
            )
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd))
                    .padding(18.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(card.back, style = TeddType.Quote, color = TeddTokens.Ink, textAlign = TextAlign.Center)
                card.note?.takeIf { it.isNotBlank() }?.let {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 14.sp, textAlign = TextAlign.Center)
                }
            }
            Spacer(modifier = Modifier.height(14.dp))
            TeddDecision(
                first = DecisionOption("Je ne savais pas", "elle repart du début"),
                second = DecisionOption("Je savais", "elle s'espace d'un cran"),
                onFirst = { model.answer(false) },
                onSecond = { model.answer(true) },
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun SessionDone(model: ReviewModel) {
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(24.dp)) {
        Text(
            text = if (model.doneToday > 0) "Séance terminée" else "Rien à réviser aujourd'hui",
            style = TeddType.Title2,
            color = TeddTokens.Ink,
        )
        Text(
            text = if (model.doneToday > 0) {
                "${model.doneToday} carte${if (model.doneToday > 1) "s" else ""} revue${if (model.doneToday > 1) "s" else ""}. " +
                    "Les suivantes reviendront d'elles-mêmes, chacune à sa date."
            } else {
                "Tes cartes sont à jour. La boîte les fera revenir au bon moment, pas avant."
            },
            style = TeddType.BodyLg,
            color = TeddTokens.InkMuted,
            modifier = Modifier.padding(top = 8.dp),
        )
        model.lastResult?.let {
            Text(it, style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp))
        }
        if (model.doneToday > 0) {
            Spacer(modifier = Modifier.height(12.dp))
            TeddButton(text = "Revoir la séance", onClick = { model.restartSession() }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
        }
    }
}

@Composable
private fun EmptyReview(onCreate: () -> Unit) {
    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(24.dp)) {
        Text("La boîte est vide", style = TeddType.Title2, color = TeddTokens.Ink)
        Text(
            text = "Une carte, c'est une question d'un côté et sa réponse de l'autre : un mot d'anglais, " +
                "une définition, une commande. Elle monte d'une case quand tu la sais, et revient plus tard. " +
                "Six cases : demain, 3 jours, une semaine, 16 jours, 35 jours, trois mois.",
            style = TeddType.BodyLg,
            color = TeddTokens.InkMuted,
            modifier = Modifier.padding(top = 8.dp, bottom = 14.dp),
        )
        TeddButton(text = "Créer la première carte", onClick = onCreate, kind = TeddButtonKind.Primary, size = TeddButtonSize.Lg)
    }
}

/** Les six cases et ce qu'elles contiennent : on voit ce qui est acquis et ce qui résiste. */
@Composable
private fun BoxesRow(boxes: List<Int>, intervals: List<Int>) {
    val max = (boxes.maxOrNull() ?: 0).coerceAtLeast(1)
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("LES ${boxes.size} CASES", style = TeddType.Overline, color = TeddTokens.InkMuted)
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            boxes.forEachIndexed { index, count ->
                Column(modifier = Modifier.weight(1f), horizontalAlignment = Alignment.CenterHorizontally) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(((count.toFloat() / max) * 48f + 6f).dp)
                            .clip(RoundedCornerShape(TeddTokens.RXs))
                            .background(if (count > 0) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs)),
                    )
                    Spacer(modifier = Modifier.height(6.dp))
                    Text("$count", style = TeddType.Mono, color = TeddTokens.Ink, fontSize = 12.sp)
                    Text(
                        text = intervals.getOrNull(index)?.let { "$it j" } ?: "—",
                        style = TeddType.Overline,
                        color = TeddTokens.InkMuted,
                    )
                }
            }
        }
    }
}

@Composable
private fun AllCardsList(model: ReviewModel) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text("TOUTES LES CARTES", style = TeddType.Overline, color = TeddTokens.InkMuted)
        model.filtered.sortedBy { it.due_date }.take(40).forEach { c ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { model.editing = c }
                    .padding(horizontal = 14.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text("${c.box}", style = TeddType.Mono, color = TeddTokens.InkMuted, fontSize = 12.sp)
                Column(modifier = Modifier.weight(1f)) {
                    Text(c.front, color = TeddTokens.Ink, fontSize = 13.5.sp, fontWeight = FontWeight.Medium, maxLines = 1)
                    Text(c.back, color = TeddTokens.InkMuted, fontSize = 12.sp, maxLines = 1)
                }
                Text(
                    text = if (c.due_date <= TeddDates.todayIso) "à revoir" else TeddDates.formatRelative(c.due_date) ?: c.due_date,
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                )
            }
        }
    }
}

@Composable
private fun FilterChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(if (selected) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 6.dp),
    ) {
        Text(label, color = if (selected) TeddTokens.OnAction else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
    }
}

/** Création et modification d'une carte : recto, verso, note, compétence. */
@Composable
private fun CardEditor(
    existing: Flashcard?,
    skills: List<com.tedd.db.Skill>,
    onClose: () -> Unit,
    onSave: (front: String, back: String, skillId: String?, note: String?) -> Unit,
    onDelete: (() -> Unit)?,
) {
    var front by remember(existing) { mutableStateOf(existing?.front ?: "") }
    var back by remember(existing) { mutableStateOf(existing?.back ?: "") }
    var note by remember(existing) { mutableStateOf(existing?.note ?: "") }
    var skillId by remember(existing) { mutableStateOf(existing?.skill_id) }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(460.dp)
                .heightIn(max = 620.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .verticalScroll(rememberScrollState())
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (existing == null) "Nouvelle carte" else "Modifier la carte", style = TeddType.Title2, color = TeddTokens.Ink)
            CardField("Recto", front, "Le mot, la question…", 64.dp) { front = it }
            CardField("Verso", back, "La réponse à retrouver", 64.dp) { back = it }
            CardField("Note", note, "Un exemple, un moyen mnémotechnique", 44.dp) { note = it }
            if (skills.isNotEmpty()) {
                Column {
                    Text("COMPÉTENCE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        skills.forEach { sk ->
                            FilterChip(sk.name, skillId == sk.id) { skillId = if (skillId == sk.id) null else sk.id }
                        }
                    }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                if (onDelete != null) {
                    TeddButton(text = "Supprimer", onClick = onDelete, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
                }
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = if (existing == null) "Créer" else "Enregistrer",
                    onClick = { onSave(front, back, skillId, note.ifBlank { null }) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = front.isNotBlank() && back.isNotBlank(),
                )
            }
        }
    }
}

@Composable
private fun CardField(label: String, value: String, placeholder: String, minHeight: androidx.compose.ui.unit.Dp, onChange: (String) -> Unit) {
    Column {
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = minHeight)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .padding(12.dp),
            contentAlignment = Alignment.TopStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onChange,
                textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink, fontSize = 15.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = TeddType.BodyLg, fontSize = 15.sp)
                    inner()
                },
            )
        }
    }
}
